"use client";
import { motion } from "framer-motion";
import { UserPlus, Wallet, LayoutGrid, TrendingUp } from "lucide-react";

const steps = [
  {
    icon: UserPlus,
    step: "01",
    title: "Sign Up",
    description:
      "Create your free account in under 2 minutes. No documentation needed to start.",
    color: "#F59E0B",
  },
  {
    icon: Wallet,
    step: "02",
    title: "Deposit",
    description:
      "Fund your wallet via crypto transfer. Minimum deposit starts at $500.",
    color: "#EA580C",
  },
  {
    icon: LayoutGrid,
    step: "03",
    title: "Choose Plan",
    description:
      "Select from Mining, Bullion, or Pharma — each with unique durations and yields.",
    color: "#DC2626",
  },
  {
    icon: TrendingUp,
    step: "04",
    title: "Earn",
    description:
      "Watch your returns accumulate automatically. Withdraw at maturity or reinvest.",
    color: "#F59E0B",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="about"
      className="overflow-hidden bg-bg-primary 2xl:px-6 xl:px-5 lg:px-4 md:px-4  px-4 2xl:py-24 xl:py-20 lg:py-16 md:py-12 py-10"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-[72px] text-center"
        >
          <span className="mb-4 inline-block rounded-full border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.1)] px-4 py-1.5 text-xs font-medium text-accent-amber sm:text-sm">
            How It Works
          </span>
          <h2 className="font-display mb-3 text-[clamp(32px,5vw,56px)]">
            Four Simple <span className="text-ember">Steps</span>
          </h2>
          <p className="mx-auto max-w-[500px] text-base text-text-muted sm:text-lg">
            Getting started with MineMe takes minutes — earning happens around
            the clock.
          </p>
        </motion.div>

        <div className="relative grid grid-cols-1 gap-y-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-y-0">
          {/* Connector Line */}
          <div className="absolute  hidden h-px lg:block">
            <div className="h-full w-full bg-linear-to-r from-transparent via-white/10 to-transparent" />
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "100%" }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="absolute inset-0 h-full bg-linear-to-r from-transparent via-[#F59E0B]/40 to-transparent"
            />
          </div>

          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="group relative px-6 text-center"
              >
                {/* Step Indicator */}
                <div
                  style={{ color: step.color }}
                  className="font-display mb-6 inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.2em] opacity-80 transition-all duration-300 group-hover:tracking-[0.3em] group-hover:opacity-100"
                >
                  <span className="h-px w-4 bg-current opacity-30" />
                  Step {step.step}
                  <span className="h-px w-4 bg-current opacity-30" />
                </div>

                {/* Icon Container with Dual Ring Effect */}
                <div className="relative mx-auto mb-8 flex h-[88px] w-[88px] items-center justify-center">
                  {/* Outer Pulsing Glow */}
                  <div
                    style={{ background: step.color }}
                    className="absolute inset-0 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-20"
                  />

                  {/* Outer Ring */}
                  <div
                    style={{ borderColor: `${step.color}20` }}
                    className="absolute inset-0 rounded-full border transition-all duration-500 group-hover:scale-110 group-hover:border-white/20"
                  />

                  {/* Inner Ring + Content */}
                  <div
                    style={{
                      background: `${step.color}15`,
                      border: `1px solid ${step.color}40`,
                    }}
                    className="relative z-10 flex h-[72px] w-[72px] items-center justify-center rounded-full transition-transform duration-500 group-hover:scale-105"
                  >
                    <Icon
                      size={28}
                      color={step.color}
                      className="transition-transform duration-500 group-hover:rotate-12"
                    />
                  </div>
                </div>

                <h3 className="font-display mb-3 text-[22px] tracking-tight text-text-primary">
                  {step.title}
                </h3>
                <p className="mx-auto max-w-[240px] text-sm leading-relaxed text-text-muted transition-colors duration-300 group-hover:text-text-primary/70">
                  {step.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
